import { Badge } from '@eriko/ui/src/components/ui/badge';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { ReconciliationMatchDTO } from '@eriko/shared-types';
import { formatCurrency } from '@eriko/shared-types';

interface Props {
  match: ReconciliationMatchDTO;
}

export function TransferPairCard({ match }: Props) {
  if (match.matchedObject.type !== 'TRANSFER') return null;
  const other = match.matchedObject.otherTransaction;
  const here = match.bankTransaction;

  const hereIsDebit = here.type === 'DEBIT';
  const debit = hereIsDebit ? here : other;
  const credit = hereIsDebit ? other : here;

  return (
    <div className="mt-6 space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-center gap-2">
        <Badge variant="warning">EXCLUDED from P&L</Badge>
        Internal transfer detected — both sides marked excluded.
      </div>

      <section className="rounded-md border border-surface-200 p-3 space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <ArrowUp className="h-4 w-4 text-rose-700" />
          <span className="font-medium text-surface-900">From (debit)</span>
        </div>
        <div className="ml-6">
          <div className="text-sm">{debit.description || debit.rawDescription || '(no description)'}</div>
          <div className="text-xs text-surface-500">
            {new Date(debit.date).toLocaleDateString()}
            {debit.bankAccountName ? ` • ${debit.bankAccountName}` : ''}
          </div>
          <div className="font-mono text-rose-700">
            -{formatCurrency(Math.abs(parseFloat(debit.amount)), 2, debit.currency ?? undefined)}
          </div>
        </div>
      </section>

      <section className="rounded-md border border-surface-200 p-3 space-y-1 text-sm">
        <div className="flex items-center gap-2">
          <ArrowDown className="h-4 w-4 text-emerald-700" />
          <span className="font-medium text-surface-900">To (credit)</span>
        </div>
        <div className="ml-6">
          <div className="text-sm">{credit.description || credit.rawDescription || '(no description)'}</div>
          <div className="text-xs text-surface-500">
            {new Date(credit.date).toLocaleDateString()}
            {credit.bankAccountName ? ` • ${credit.bankAccountName}` : ''}
          </div>
          <div className="font-mono text-emerald-700">
            +{formatCurrency(Math.abs(parseFloat(credit.amount)), 2, credit.currency ?? undefined)}
          </div>
        </div>
      </section>

      <section className="text-xs text-surface-500">
        Confidence: {(match.matchConfidence * 100).toFixed(0)}% • Reason: transfer detected
      </section>
    </div>
  );
}
